import type React from 'react'
import type { IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton'
import {
  type ThemeOptions,
  type TypographyVariantsOptions,
  alpha,
  createTheme,
} from '@mui/material/styles'
import type {} from '@mui/x-date-pickers/themeAugmentation'

declare module '@mui/material/styles' {
  interface CommonColors {
    transparent: string
  }
}

import IconButton from './components/internal/IconButton'
import {
  borderRadius,
  borders,
  colors,
  darkTokens,
  inputShadows,
  lightTokens,
  muiShadows,
  shadows,
  spacing,
  switchTokens,
  transitions,
  typography as typographyTokens,
} from './tokens/internal'

// Typography variants
const typography = {
  fontFamily: typographyTokens.fontFamily.body,
  h1: {
    fontFamily: typographyTokens.fontFamily.heading,
    fontSize: typographyTokens.fontSize.mega,
    fontWeight: typographyTokens.fontWeight.regular,
    lineHeight: typographyTokens.lineHeight.tight,
  },
  h2: {
    fontFamily: typographyTokens.fontFamily.heading,
    fontSize: typographyTokens.fontSize.huge,
    fontWeight: typographyTokens.fontWeight.regular,
    lineHeight: typographyTokens.lineHeight.tight,
  },
  h3: {
    fontFamily: typographyTokens.fontFamily.heading,
    fontSize: typographyTokens.fontSize.xxl,
    fontWeight: typographyTokens.fontWeight.regular,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  h4: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.lg,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  h5: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.md,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  body1: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.sm,
    fontWeight: typographyTokens.fontWeight.regular,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  body2: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.xs,
    fontWeight: typographyTokens.fontWeight.regular,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  subtitle1: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.sm,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  subtitle2: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.xs,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  caption: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.xxs,
    fontWeight: typographyTokens.fontWeight.regular,
    lineHeight: typographyTokens.lineHeight.normal,
  },
  overline: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.micro,
    fontWeight: typographyTokens.fontWeight.medium,
    letterSpacing: typographyTokens.letterSpacing.caps,
    textTransform: 'uppercase',
  },
  label: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.xs,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.tight,
  },
  labelXs: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.micro,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.compact,
  },
  labelSm: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.xs,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.compact,
  },
  labelMd: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.sm,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.tight,
  },
  labelLg: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.xxl,
    fontWeight: typographyTokens.fontWeight.medium,
    lineHeight: typographyTokens.lineHeight.compact,
  },
  link: {
    fontFamily: typographyTokens.fontFamily.body,
    fontSize: typographyTokens.fontSize.sm,
    fontWeight: typographyTokens.fontWeight.medium,
    letterSpacing: typographyTokens.letterSpacing.relaxed,
    lineHeight: typographyTokens.lineHeight.relaxed,
    textTransform: 'none',
  },
}

const createCustomInternalTheme = (mode: 'light' | 'dark'): ThemeOptions => {
  const tokens = mode === 'light' ? lightTokens : darkTokens

  // Shared styles for selection controls (Checkbox, Radio)
  const selectionControlStyles = {
    root: {
      alignSelf: 'center',
      color: mode === 'light' ? colors.neutral[200] : alpha(colors.neutral[0], 0.23),
      '&.Mui-checked': {
        color: tokens.primary.main,
      },
      '&.Mui-disabled': {
        color: tokens.action.disabled,
      },
    },
  }

  const themeOptions: ThemeOptions = {
    typography: typography as TypographyVariantsOptions,
    shadows: muiShadows as unknown as ThemeOptions['shadows'],
    components: {
      MuiPaper: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily.body,
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            '& path': {
              strokeWidth: 0.5,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            position: 'relative',
            '&:focus-visible': {
              boxShadow: `0 0 0 2px ${tokens.background.default}, 0 0 0 4px ${colors.blue[600]}, 0 0 0 6px ${tokens.focusRing}`,
              zIndex: 3,
            },
          },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            position: 'relative',
            '&:focus-visible': {
              boxShadow: `0 0 0 2px ${tokens.background.default}, 0 0 0 4px ${colors.blue[600]}, 0 0 0 6px ${tokens.focusRing}`,
              zIndex: 3,
            },
          },
        },
      },
      MuiToggleButtonGroup: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.background.background3,
            borderRadius: borderRadius.sm,
            padding: spacing.micro,
            gap: spacing.xxs,
            height: spacing.lg,
            '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
              borderTopRightRadius: borderRadius.sm,
              borderBottomRightRadius: borderRadius.sm,
            },
            '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
              borderTopLeftRadius: borderRadius.sm,
              borderBottomLeftRadius: borderRadius.sm,
              marginLeft: 0,
              borderLeft: 'none',
            },
          },
          grouped: {
            borderRadius: borderRadius.sm,
            '&:not(:first-of-type)': {
              marginLeft: 0,
              borderLeft: 'none',
              borderTopLeftRadius: borderRadius.sm,
              borderBottomLeftRadius: borderRadius.sm,
            },
            '&:not(:last-of-type)': {
              borderTopRightRadius: borderRadius.sm,
              borderBottomRightRadius: borderRadius.sm,
            },
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: borderRadius.sm,
            color: tokens.text.primary,
            gap: spacing.micro,
            textTransform: 'none',
            whiteSpace: 'nowrap',
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.sm,
            fontWeight: typographyTokens.fontWeight.regular,
            lineHeight: typographyTokens.lineHeight.normal,
            '&.Mui-selected': {
              backgroundColor: tokens.background.default,
              color: tokens.primary.main,
              '&:hover': {
                backgroundColor: tokens.background.default,
              },
            },
            '&.Mui-disabled': {
              color: tokens.action.disabled,
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            fontSize: typographyTokens.fontSize.md,
            fontWeight: typographyTokens.fontWeight.regular,
            color: tokens.text.secondary,
            textTransform: 'none',
            '&.Mui-selected': {
              color: tokens.primary.main,
            },
            '&.Mui-disabled': {
              color: tokens.text.disabled,
              cursor: 'not-allowed',
              pointerEvents: 'auto',
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.xs,
            fontWeight: typographyTokens.fontWeight.medium,
            lineHeight: '18px',
            color: mode === 'light' ? colors.neutral[700] : colors.neutral[600],
            marginBottom: '8px',
            position: 'relative',
            transform: 'none',
            '&.Mui-focused': {
              color: mode === 'light' ? colors.neutral[700] : colors.neutral[600],
            },
            '&.Mui-disabled': {
              color: tokens.text.disabled,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.divider,
              borderWidth: borders.width.thin,
              transition: `border-color ${transitions.duration.normal} ${transitions.easing.easeInOut}`,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: tokens.primary.main,
              borderWidth: borders.width.medium,
            },
            '&.Mui-focused': {
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: tokens.primary.main,
                boxShadow: `0 0 0 ${borders.width.medium} ${colors.primary[100]}`,
              },
            },
          },
          notchedOutline: {
            top: 0,
            '& legend': {
              display: 'none',
            },
          },
          input: {
            '&::placeholder': {
              color: tokens.text.disabled,
              opacity: 1,
            },
            '&.Mui-disabled': {
              color: tokens.text.disabled,
              WebkitTextFillColor: tokens.text.disabled,
            },
          },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: {
            '& .MuiInputLabel-root': {
              position: 'relative',
              transform: 'none',
              marginBottom: spacing.xxs,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontSize: typographyTokens.fontSize.xs,
            display: 'block',
            fontFamily: typographyTokens.fontFamily.body,
            fontWeight: typographyTokens.fontWeight.medium,
            marginBottom: spacing.xxs,
            color: tokens.text.secondary,
            '&.Mui-focused': {
              color: tokens.text.secondary,
            },
            '&.Mui-disabled': {
              color: tokens.text.disabled,
            },
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          arrow: false,
        },
        styleOverrides: {
          tooltip: {
            backgroundColor: alpha(tokens.background.tooltip, colors.opacity.tooltipBackground),
            color: tokens.background.background1,
            fontSize: typographyTokens.fontSize.xxs,
            fontWeight: typographyTokens.fontWeight.regular,
            lineHeight: typographyTokens.lineHeight.tight,
            padding: `${spacing.nano} ${spacing.xxs}`,
            borderRadius: borderRadius.sm,
          },
          popper: {
            '&[data-popper-placement*="bottom"] .MuiTooltip-tooltip': {
              marginTop: spacing.micro,
            },
            '&[data-popper-placement*="top"] .MuiTooltip-tooltip': {
              marginBottom: spacing.micro,
            },
            '&[data-popper-placement*="left"] .MuiTooltip-tooltip': {
              marginRight: spacing.micro,
            },
            '&[data-popper-placement*="right"] .MuiTooltip-tooltip': {
              marginLeft: spacing.micro,
            },
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            margin: 0,
            gap: '8px',
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          sizeSmall: {
            width: switchTokens.small.width,
            height: switchTokens.small.height,
            '& .MuiSwitch-switchBase': {
              margin: switchTokens.small.margin,
              '&.Mui-checked': {
                transform: `translateX(${switchTokens.small.translateX})`,
              },
            },
            '& .MuiSwitch-thumb': {
              width: switchTokens.small.thumbSize,
              height: switchTokens.small.thumbSize,
            },
            '& .MuiSwitch-track': {
              borderRadius: `calc(${switchTokens.small.height} / 2)`,
            },
          },
          root: {
            width: switchTokens.medium.width,
            height: switchTokens.medium.height,
            padding: spacing.none,
            '& .MuiSwitch-switchBase': {
              padding: spacing.none,
              margin: switchTokens.medium.margin,
              transitionDuration: transitions.duration.slow,
              '&.Mui-checked': {
                transform: `translateX(${switchTokens.medium.translateX})`,
                color: tokens.switch.thumb,
                '& + .MuiSwitch-track': {
                  backgroundColor: tokens.primary.main,
                  opacity: 1,
                  border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  backgroundColor: tokens.border,
                  opacity: 1,
                },
              },
              '&.Mui-disabled .MuiSwitch-thumb': {
                color: tokens.switch.thumbDisabled,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                backgroundColor: tokens.border,
                opacity: 1,
              },
              '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: tokens.primary.main,
                border: `${borders.width.thick} solid ${tokens.switch.thumb}`,
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: switchTokens.medium.thumbSize,
              height: switchTokens.medium.thumbSize,
              boxShadow: shadows.xs,
            },
            '& .MuiSwitch-track': {
              borderRadius: `calc(${switchTokens.medium.height} / 2)`,
              backgroundColor: tokens.switch.track,
              opacity: 1,
            },
            '& .MuiButtonBase-root.MuiSwitch-switchBase:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            border: 'none',
            borderRadius: 0,
            backgroundColor: tokens.background.default,
          },
        },
      },
      MuiTableHead: {
        styleOverrides: {
          root: {
            backgroundColor: tokens.background.background2,
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: `1px solid ${tokens.border}`,
          },
          head: {
            backgroundColor: tokens.background.background2,
            lineHeight: typographyTokens.lineHeight.relaxed,
          },
          body: {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: typographyTokens.lineHeight.relaxed,
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: `background-color ${transitions.duration.fast} ${transitions.easing.easeInOut}`,
            '&.MuiTableRow-hover:hover': {
              backgroundColor: tokens.action.hover,
            },
            '&.Mui-selected': {
              backgroundColor: tokens.primary.states.selected,
              '&:hover': {
                backgroundColor: tokens.primary.states.selected,
              },
            },
          },
          head: {
            '&.MuiTableRow-hover:hover': {
              backgroundColor: 'transparent',
            },
          },
        },
      },
      MuiTableSortLabel: {
        styleOverrides: {
          root: {
            color: 'inherit',
            '&:hover': {
              color: tokens.text.primary,
            },
            '&.Mui-active': {
              '& .MuiTableSortLabel-icon': {
                color: tokens.action.active,
              },
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.full,
            border: 'none',
          },
          label: {
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.xs,
            fontWeight: typographyTokens.fontWeight.regular,
            lineHeight: typographyTokens.lineHeight.normal,
            letterSpacing: typographyTokens.letterSpacing.normal,
          },
          icon: {
            marginLeft: spacing.xs,
            marginRight: spacing.negativeMicro,
            fontSize: typographyTokens.fontSize.md,
          },
          deleteIcon: {
            fontSize: typographyTokens.fontSize.md,
          },
          sizeSmall: {
            height: typographyTokens.fontSize.xl,
            '& .MuiChip-label': {
              fontSize: typographyTokens.fontSize.xxs,
              lineHeight: typographyTokens.lineHeight.tight,
            },
            '& .MuiChip-icon': {
              fontSize: typographyTokens.fontSize.xs,
              marginLeft: spacing.xxs,
              marginRight: spacing.negativePico,
            },
            '& .MuiChip-deleteIcon': {
              fontSize: typographyTokens.fontSize.xs,
              marginLeft: spacing.xxs,
              marginRight: spacing.micro,
            },
          },
        },
      },
      MuiListSubheader: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.xs,
            fontWeight: typographyTokens.fontWeight.semibold,
            lineHeight: typographyTokens.lineHeight.tight,
            letterSpacing: typographyTokens.letterSpacing.tight,
            paddingTop: spacing.xs,
            paddingBottom: spacing.xxs,
          },
        },
      },
      // --- Menu / MenuItem / Select overrides ---
      MuiMenu: {
        styleOverrides: {
          paper: {
            borderRadius: borderRadius.sm,
            boxShadow: shadows.menu,
          },
          list: {
            paddingTop: spacing.micro,
            paddingBottom: spacing.micro,

            // Compact icon spacing only inside Menu (not regular Lists).
            // Double ampersand boosts specificity above MenuItem's built-in
            // `& .MuiListItemIcon-root { minWidth: 36 }` rule, which otherwise
            // wins or loses depending on emotion style insertion order.
            '&& .MuiListItemIcon-root': {
              minWidth: 'auto',
              marginRight: spacing.xxs,
              '& .material-symbols-rounded, & .material-symbols-outlined': {
                fontSize: typographyTokens.fontSize.lg,
                width: typographyTokens.fontSize.lg,
                height: typographyTokens.fontSize.lg,
              },
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            margin: spacing.nano,
            borderRadius: borderRadius.sm,
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.sm,
            fontWeight: typographyTokens.fontWeight.regular,
            lineHeight: typographyTokens.lineHeight.relaxed,
            color: tokens.text.primary,
            '&:hover': {
              backgroundColor: tokens.action.hover,
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: tokens.action.hover,
              },
            },
            '&.Mui-selected': {
              color: tokens.text.primary,
              backgroundColor: tokens.primary.lighter,
              '&:hover': {
                backgroundColor: tokens.primary.light,
              },
            },
            // Destructive menu item styling
            '&.destructive': {
              color: tokens.error.main,
              '& .MuiListItemIcon-root': {
                color: tokens.error.main,
              },
              '&:hover': {
                backgroundColor: tokens.error.lighter,
              },
            },
          },
        },
      },
      MuiSelect: {},
      // --- Dialog overrides ---
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: borderRadius.md,
            boxShadow: shadows.menu,
          },
          container: {
            fontFamily: typographyTokens.fontFamily.body,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.xl,
            fontWeight: typographyTokens.fontWeight.medium,
            lineHeight: typographyTokens.lineHeight.normal,
            padding: `${spacing.sm} ${spacing.sm} ${spacing.xs}`,
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: `${spacing.xs} ${spacing.sm} ${spacing.sm}`,
            position: 'sticky' as const,
            bottom: 0,
            backgroundColor: tokens.background.default,
            borderTop: `${borders.width.thin} solid ${tokens.border}`,
          },
        },
      },
      // --- Drawer overrides ---
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRadius: borderRadius.none,
          },
        },
      },
      // --- Checkbox / Radio overrides ---
      MuiCheckbox: {
        styleOverrides: {
          ...selectionControlStyles,
          sizeSmall: {
            padding: spacing.pico,
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          root: {
            ...selectionControlStyles.root,
            '&:not(.Mui-checked):hover': {
              backgroundColor: tokens.action.hover,
            },
            '&:not(.Mui-checked).Mui-focusVisible': {
              backgroundColor: tokens.action.focus,
            },
          },
        },
      },
      MuiFormGroup: {
        styleOverrides: {
          root: {
            gap: spacing.xxs,
          },
        },
      },
      // --- Slider overrides ---
      MuiSlider: {
        styleOverrides: {
          valueLabel: {
            backgroundColor: tokens.slider.valueLabelBackground,
            color: tokens.slider.valueLabelText,
            fontSize: typographyTokens.fontSize.xxs,
            fontWeight: typographyTokens.fontWeight.regular,
            lineHeight: typographyTokens.lineHeight.tight,
            padding: `${spacing.micro} ${spacing.xxs}`,
            borderRadius: borderRadius.sm,
          },
        },
      },
      // --- LinearProgress overrides ---
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.full,
            backgroundColor: tokens.border,
          },
          bar: {
            borderRadius: borderRadius.full,
          },
        },
      },
      // --- Stepper overrides ---
      MuiStepIcon: {
        styleOverrides: {
          root: {
            color: tokens.text.disabled,
            fontSize: typographyTokens.fontSize.xl,
            '&.Mui-active': {
              color: tokens.primary.main,
            },
            '&.Mui-completed': {
              color: tokens.primary.main,
            },
            text: {
              fill: tokens.background.default,
              fontFamily: typographyTokens.fontFamily.body,
              fontSize: typographyTokens.fontSize.xxs,
              fontWeight: typographyTokens.fontWeight.regular,
            },
          },
        },
      },
      MuiStepConnector: {
        styleOverrides: {
          line: {
            borderColor: tokens.stepper.connector,
            borderWidth: borders.width.medium,
          },
        },
      },
      MuiStepContent: {
        styleOverrides: {
          root: {
            borderColor: tokens.stepper.connector,
            borderWidth: borders.width.medium,
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          label: {
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.xs,
            fontWeight: typographyTokens.fontWeight.regular,
            color: tokens.text.secondary,
            '&.Mui-active': {
              fontWeight: typographyTokens.fontWeight.medium,
              color: tokens.text.primary,
            },
            '&.Mui-completed': {
              fontWeight: typographyTokens.fontWeight.medium,
              color: tokens.text.primary,
            },
          },
        },
      },
      MuiMobileStepper: {
        styleOverrides: {
          root: {
            padding: spacing.xxs,
            backgroundColor: 'transparent',
          },
          dotActive: {
            backgroundColor: tokens.primary.main,
          },
          dot: {
            backgroundColor: tokens.stepper.connector,
          },
        },
      },
      // --- Avatar overrides ---
      MuiAvatar: {
        styleOverrides: {
          root: {
            fontFamily: typographyTokens.fontFamily.body,
            fontWeight: typographyTokens.fontWeight.medium,
          },
        },
      },
      // --- Badge overrides ---
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontFamily: typographyTokens.fontFamily.body,
            fontSize: typographyTokens.fontSize.xxs,
            fontWeight: typographyTokens.fontWeight.regular,
            lineHeight: '20px',
          },
        },
      },
      // --- Alert overrides ---
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: borderRadius.md,
          },
          message: {
            fontFamily: typographyTokens.fontFamily.body,
            '& .MuiAlertTitle-root': {
              fontSize: typographyTokens.fontSize.sm,
              fontWeight: typographyTokens.fontWeight.semibold,
              lineHeight: typographyTokens.lineHeight.relaxed,
            },
          },
        },
      },
      // --- List / ListItemButton overrides ---
      MuiList: {
        styleOverrides: {
          root: {
            padding: 0,
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: tokens.background.background2,
            },
            '&.Mui-selected': {
              backgroundColor: tokens.primary.lighter,
              '&:hover': {
                backgroundColor: tokens.primary.light,
              },
            },
          },
        },
      },
      MuiPickersOutlinedInput: {
        styleOverrides: {
          root: {
            marginTop: '0 !important',
            '&:hover:not(.Mui-disabled):not(.Mui-error) .MuiPickersOutlinedInput-notchedOutline': {
              boxShadow: inputShadows.hover,
            },
            '&.Mui-focused': {
              '& .MuiPickersOutlinedInput-notchedOutline': {
                boxShadow: `${inputShadows.focus}, 0 0 0 ${borders.width.medium} ${colors.primary[100]}`,
                border: 'none',
              },
            },
            '&.Mui-error .MuiPickersOutlinedInput-notchedOutline': {
              boxShadow: inputShadows.error,
            },
            '&.Mui-disabled': {
              backgroundColor: tokens.input.disabledBackground,
              '& .MuiPickersOutlinedInput-notchedOutline': {
                boxShadow: inputShadows.disabled,
              },
            },
          },
          notchedOutline: {
            top: 0,
            border: 'none',
            boxShadow: inputShadows.default,
            borderRadius: borderRadius.sm,
            transition: `box-shadow ${transitions.duration.fast} ${transitions.easing.easeInOut}`,
            '& legend': {
              display: 'none',
            },
          },
        },
      },
      MuiPickersLayout: {
        styleOverrides: {
          root: {
            '& .MuiPickersLayout-actionBar': {
              padding: spacing.xxs,
            },
          },
        },
      },
      MuiDatePicker: {
        defaultProps: {
          slots: {
            clearButton: IconButton as unknown as React.ElementType<MuiIconButtonProps>,
            openPickerButton: IconButton as unknown as React.ElementType<MuiIconButtonProps>,
            previousIconButton: IconButton as React.ElementType,
            nextIconButton: IconButton as React.ElementType,
            switchViewButton: IconButton as React.ElementType,
          },
          slotProps: {
            field: { clearable: true },
            clearButton: { size: 'small', color: 'default' },
            openPickerButton: { size: 'small', color: 'default' },
            previousIconButton: { size: 'small', color: 'default' },
            nextIconButton: { size: 'small', color: 'default' },
            switchViewButton: { size: 'small', color: 'default' },
            textField: {
              variant: 'outlined',
              size: 'small',
              sx: {
                '& .MuiOutlinedInput-notchedOutline legend': {
                  display: 'none',
                },
              },
            },
          },
        },
      },
      MuiTimePicker: {
        defaultProps: {
          slots: {
            clearButton: IconButton as unknown as React.ElementType<MuiIconButtonProps>,
            openPickerButton: IconButton as unknown as React.ElementType<MuiIconButtonProps>,
            previousIconButton: IconButton as React.ElementType,
            nextIconButton: IconButton as React.ElementType,
          },
          slotProps: {
            field: { clearable: true },
            clearButton: { size: 'small', color: 'default' },
            openPickerButton: { size: 'small', color: 'default' },
            previousIconButton: { size: 'small', color: 'default' },
            nextIconButton: { size: 'small', color: 'default' },
            textField: {
              variant: 'outlined',
              size: 'small',
              sx: {
                '& .MuiOutlinedInput-notchedOutline legend': {
                  display: 'none',
                },
              },
            },
          },
        },
      },
      MuiDateTimePicker: {
        defaultProps: {
          slots: {
            clearButton: IconButton as unknown as React.ElementType<MuiIconButtonProps>,
            openPickerButton: IconButton as unknown as React.ElementType<MuiIconButtonProps>,
            previousIconButton: IconButton as React.ElementType,
            nextIconButton: IconButton as React.ElementType,
            switchViewButton: IconButton as React.ElementType,
          },
          slotProps: {
            field: { clearable: true },
            clearButton: { size: 'small', color: 'default' },
            openPickerButton: { size: 'small', color: 'default' },
            previousIconButton: { size: 'small', color: 'default' },
            nextIconButton: { size: 'small', color: 'default' },
            switchViewButton: { size: 'small', color: 'default' },
            textField: {
              variant: 'outlined',
              size: 'small',
              sx: {
                '& .MuiOutlinedInput-notchedOutline legend': {
                  display: 'none',
                },
              },
            },
          },
        },
      },
    },
    palette: {
      mode,
      ...(tokens as Record<string, unknown>),
    },
  }

  return themeOptions
}

export const internalLightTheme = createTheme(createCustomInternalTheme('light'))
export const internalDarkTheme = createTheme(createCustomInternalTheme('dark'))
